import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import ServiceCard from './ServiceCard';
import { getServices, getHomepageSettings } from '../../../services/contentApi';

const ServicesSection = ({ onGetQuote }) => {
  const [services, setServices] = useState([]);
  const [homepageSettings, setHomepageSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState('all');

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [servicesData, homepageData] = await Promise.all([
        getServices(),
        getHomepageSettings()
      ]);

      // Filter active services
      const activeServices = servicesData.filter(service => service.status === 'active');
      setServices(activeServices);
      setHomepageSettings(homepageData);
    } catch (error) {
      console.error('Error loading services:', error);
      // Fallback to empty array
      setServices([]);
    } finally {
      setLoading(false);
    }
  };

  const categories = [
    { id: 'all', name: 'All Services', count: services.length },
    { id: 'consultants', name: 'Design Services', count: services.filter(s => s.category === 'consultants').length },
    { id: 'regulatory', name: 'Approvals', count: services.filter(s => s.category === 'regulatory').length }
  ];

  const filteredServices = selectedCategory === 'all'
    ? services
    : services.filter(service => service.category === selectedCategory);

  return (
    <section id="services" className="py-16 bg-muted/20">
      <div className="container mx-auto px-4">
        {/* Section Header */}
        <div className="text-center mb-12 animate-fade-in-up">
          <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">
            {homepageSettings?.sections?.services?.heading || 'Our Services'}
          </h2>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
            {homepageSettings?.sections?.services?.subheading || 'Comprehensive construction and regulatory services to bring your vision to life'}
          </p>
        </div>

        {/* Category Filter */}
        <div className="flex justify-center mb-8">
          <div className="flex space-x-2 bg-card rounded-lg p-1 border border-border">
            {categories.map((category) => (
              <button
                key={category.id}
                onClick={() => setSelectedCategory(category.id)}
                className={`px-4 py-2 rounded-md text-sm font-medium transition-all duration-200 ${selectedCategory === category.id
                    ? 'bg-primary text-primary-foreground shadow-sm'
                    : 'text-muted-foreground hover:text-foreground hover:bg-muted'
                  }`}
              >
                {category.name}
                <span className="ml-2 text-xs opacity-75">({category.count})</span>
              </button>
            ))}
          </div>
        </div>

        {/* Loading State */}
        {loading && (
          <div className="flex justify-center items-center py-12">
            <div className="text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
              <p className="text-gray-600">Loading services...</p>
            </div>
          </div>
        )}

        {/* Services Grid */}
        {!loading && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredServices.map((service) => (
              <ServiceCard
                key={service.id}
                service={service}
                onGetQuote={onGetQuote}
              />
            ))}
          </div>
        )}

        {/* Empty State */}
        {!loading && filteredServices.length === 0 && (
          <div className="text-center py-12">
            <Icon name="Search" size={48} className="text-muted-foreground mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-foreground mb-2">No services found</h3>
            <p className="text-muted-foreground">
              Try selecting a different category
            </p>
          </div>
        )}
      </div>
    </section>
  );
};

export default ServicesSection; 